import { z } from "zod";
import { emailStatusSchema } from "../event-reports/emailStatus.js";
// 10 photos max, ordre d'ajout, légende facultative (#1231).
export const MAX_JARDINIER_PHOTOS = 10;
/**
 * Même borne anti-abus que CertificatRamonage.REMARQUES_MAX_LENGTH — le PDF repagine
 * automatiquement un texte long (voir JARDINIER_ACTIONS_REALISEES_MAX_LENGTH ci-dessous),
 * la borne n'est donc pas une contrainte de mise en page.
 */
export const JARDINIER_FIELD_MAX_LENGTH = 1000;
// Borne dédiée, plus large que JARDINIER_FIELD_MAX_LENGTH, pour laisser la place à une
// description détaillée sans ouvrir la porte à un pavé disproportionné (#1326).
export const JARDINIER_ACTIONS_REALISEES_MAX_LENGTH = 5000;
/**
 * Les photos ne vivent que dans le PDF généré (blob de `report-file`) — pas de stockage
 * binaire séparé. Seule la légende est portée par `data`, pour permettre de retrouver le
 * contenu textuel d'un compte rendu sans redécoder le PDF (#1231).
 */
export const jardinierPhotoSchema = z.object({
    caption: z.string().max(JARDINIER_FIELD_MAX_LENGTH).optional(),
});
export const jardinierReportSchema = z
    .object({
    etatPotagerArrivee: z.string().max(JARDINIER_FIELD_MAX_LENGTH).optional(),
    actionsRealisees: z.string().max(JARDINIER_ACTIONS_REALISEES_MAX_LENGTH).optional(),
    legumesRecolteEtPlantations: z.string().max(JARDINIER_FIELD_MAX_LENGTH).optional(),
    observationsComplementaires: z.string().max(JARDINIER_FIELD_MAX_LENGTH).optional(),
    arrosageVerifie: z.boolean().default(false),
    photos: z.array(jardinierPhotoSchema).max(MAX_JARDINIER_PHOTOS).default([]),
    // Signatures (repris du pattern attestation de ramonage).
    signatureTechnicien: z.string(),
    signatureTechnicienDate: z.string(),
    signatureClient: z.string().optional(),
    signatureClientDate: z.string().optional(),
    clientRefuseSigner: z.boolean().default(false),
    /**
     * Statut d'envoi : jamais envoyé par le formulaire de création, posé par le backend
     * (send()/resend()/webhook de bounce) — d'où optionnel dans ce schéma partagé, même
     * pattern que CertificatRamonage (#1270).
     */
    emailStatus: emailStatusSchema.nullable().optional(),
    emailMessage: z.string().nullable().optional(),
})
    .refine((data) => Boolean(data.etatPotagerArrivee?.trim()) ||
    Boolean(data.actionsRealisees?.trim()) ||
    Boolean(data.legumesRecolteEtPlantations?.trim()) ||
    Boolean(data.observationsComplementaires?.trim()) ||
    data.arrosageVerifie ||
    data.photos.length > 0, {
    message: "Le compte rendu ne peut pas être entièrement vide",
    path: ["etatPotagerArrivee"],
});
/**
 * Payload schema : sans `emailStatus`/`emailMessage` (jamais posés par le client — un payload
 * de création qui les porterait serait ignoré ici, pas juste toléré), même garde-fou que
 * `certificatRamonagePayloadSchema` (#1270). Le `.refine()` métier reste appliqué
 * côté service (`jardinierReportSchema.parse`), pas dupliqué ici.
 */
export const jardinierReportPayloadSchema = jardinierReportSchema
    .innerType()
    .omit({ emailStatus: true, emailMessage: true });
export const JARDINIER_OMITTED_PHOTOS_HEADER = "x-jardinier-omitted-photos";
export const JARDINIER_IMAGE_MAX_LENGTH = Math.ceil((5 * 1024 * 1024) / 3) * 4 + 23;
const jardinierRenderImageSchema = z
    .string()
    .max(JARDINIER_IMAGE_MAX_LENGTH)
    .refine((value) => /^data:image\/(png|jpeg);base64,.+/.test(value), "Une image PNG ou JPEG est requise");
const jardinierRenderReportSchema = jardinierReportPayloadSchema
    .extend({
    signatureTechnicien: jardinierRenderImageSchema,
    signatureTechnicienDate: z.string().datetime({ offset: true }),
    signatureClient: jardinierRenderImageSchema.optional(),
    signatureClientDate: z.string().datetime({ offset: true }).optional(),
})
    .superRefine((report, context) => {
    const result = jardinierReportSchema.safeParse(report);
    if (result.success === false) {
        for (const issue of result.error.issues) {
            context.addIssue(issue);
        }
    }
});
const jardinierRenderFields = {
    report: jardinierRenderReportSchema,
    photos: z.array(jardinierRenderImageSchema).max(MAX_JARDINIER_PHOTOS),
};
function matchingJardinierPhotos(payload, context) {
    if (payload.photos.length !== payload.report.photos.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["photos"],
            message: "Chaque photo du compte rendu doit avoir une image",
        });
    }
}
export const jardinierReportRenderSchema = z
    .object(jardinierRenderFields)
    .superRefine(matchingJardinierPhotos);
export const jardinierReportFinalizeSchema = z
    .object({ ...jardinierRenderFields, sendEmail: z.boolean() })
    .superRefine(matchingJardinierPhotos);
export const FinalizeJardinierReportResponseSchema = z.object({
    report: jardinierReportSchema,
    fileName: z.string(),
    outcome: z.enum(["created", "recovered", "already_exists"]),
    emailAttempt: z.enum(["sent", "skipped", "failed", "not_attempted"]),
    emailMessage: z.string().optional(),
});
/**
 * Projection pour la section Documents de la fiche client (#1278), miroir de
 * `CertificatRamonageDocumentSchema` — délibérément sans les champs métier du compte rendu.
 */
export const JardinierReportDocumentSchema = z.object({
    eventId: z.string(),
    fileName: z.string(),
    reportDate: z.coerce.date(),
    eventDate: z.coerce.date(),
    emailStatus: emailStatusSchema.nullable().optional(),
});
//# sourceMappingURL=JardinierReport.js.map